import { useMemo, useState } from 'react';
import { Panel } from '../../../components/panels/Panel';
import { VERALLIA_DASHBOARD_MOCK_DETECTIONS } from '../../../data/verallia-dashboard-mock-data';
import {
  filterVeralliaDashboardDetections,
  getVeralliaDashboardByDay,
  getVeralliaDashboardByHour,
  getVeralliaDashboardByLine,
  getVeralliaDashboardByMonth,
  getVeralliaDashboardByShift,
  getVeralliaDashboardByWeek,
  getVeralliaDashboardDefaultFilters,
  getVeralliaDashboardDefectClassification,
  getVeralliaDashboardLinesReporting,
} from '../../../services/dashboard/verallia-dashboard-service';
import { VeralliaDashboardFilters } from './components/VeralliaDashboardFilters';
import { VeralliaDashboardKpi } from './components/VeralliaDashboardBlocks';
import {
  graphColors,
  VeralliaDashboardBarChart,
  VeralliaDashboardDefectClassification,
} from './components/VeralliaDashboardCharts';

export const VeralliaDashboardStatistics = () => {
  const [filters, setFilters] = useState(() => getVeralliaDashboardDefaultFilters(VERALLIA_DASHBOARD_MOCK_DETECTIONS));

  const filteredDetections = useMemo(() => {
    return filterVeralliaDashboardDetections(VERALLIA_DASHBOARD_MOCK_DETECTIONS, filters);
  }, [filters]);

  const classification = useMemo(() => {
    return getVeralliaDashboardDefectClassification(filteredDetections);
  }, [filteredDetections]);

  const byHour = useMemo(() => {
    return getVeralliaDashboardByHour(filteredDetections, filters.start, filters.end);
  }, [filteredDetections, filters.start, filters.end]);

  const byDay = useMemo(() => {
    return getVeralliaDashboardByDay(filteredDetections, filters.start, filters.end);
  }, [filteredDetections, filters.start, filters.end]);

  const byWeek = useMemo(() => {
    return getVeralliaDashboardByWeek(filteredDetections, filters.start, filters.end);
  }, [filteredDetections, filters.start, filters.end]);

  const byMonth = useMemo(() => {
    return getVeralliaDashboardByMonth(filteredDetections, filters.start, filters.end);
  }, [filteredDetections, filters.start, filters.end]);

  const byShift = useMemo(() => {
    return getVeralliaDashboardByShift(filteredDetections, filters.start, filters.end);
  }, [filteredDetections, filters.start, filters.end]);

  const byLine = useMemo(() => {
    return getVeralliaDashboardByLine(filteredDetections);
  }, [filteredDetections]);

  return (
    <Panel title="STATISTICS">
      <div className="mx-auto flex w-11/12 flex-col gap-4 py-6">
        <VeralliaDashboardFilters detections={VERALLIA_DASHBOARD_MOCK_DETECTIONS} value={filters} onChange={setFilters}/>

        <div className="grid grid-cols-2 gap-4">
          <VeralliaDashboardKpi label="Detections in scope" value={filteredDetections.length}/>
          <VeralliaDashboardKpi label="Lines reporting" value={getVeralliaDashboardLinesReporting(filteredDetections)}/>
        </div>

        <VeralliaDashboardDefectClassification classification={classification} fixedHeight/>

        <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
          <VeralliaDashboardBarChart title="By hour" subtitle="Average detections by hour" labels={byHour.labels} values={byHour.values} color={graphColors[0]}/>
          <VeralliaDashboardBarChart title="By day" subtitle="Average detections by day of month" labels={byDay.labels} values={byDay.values} color={graphColors[1]}/>
          <VeralliaDashboardBarChart title="By week" subtitle="Average detections by ISO week" labels={byWeek.labels} values={byWeek.values} color={graphColors[2]}/>
          <VeralliaDashboardBarChart title="By month" subtitle="Average detections by month" labels={byMonth.labels} values={byMonth.values} color={graphColors[3]}/>
          <VeralliaDashboardBarChart title="By shift" subtitle="Average detections per shift" labels={byShift.labels} values={byShift.values} color={graphColors[5]}/>
          <VeralliaDashboardBarChart title="By line" subtitle="Total detections" labels={byLine.labels} values={byLine.values} color={graphColors[6]}/>
        </div>
      </div>
    </Panel>
  );
};